import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/config/branding";

export default function RegisterPage() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const result = await register(email, password, name, company);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="register-page">
      {/* Logo Bar */}
      <div className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] px-6 py-3">
        <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" data-testid="page-logo" />
      </div>

      <div className="flex-1 flex">
        <div className="hidden lg:flex lg:w-1/2 auth-hero items-end justify-start p-12">
          <div className="max-w-md text-white">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Join Self Certify UK
            </h1>
            <p className="text-base leading-relaxed text-white/60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Create your free account to start generating electrical certificates, gas safety records, MCS documents, PAT reports, and building notifications.
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-12" style={{ backgroundColor: '#F5F5F7' }}>
          <div className="w-full max-w-md animate-fade-in-up">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
              Create Account
            </h2>
            <p className="text-sm mb-8" style={{ color: '#8E8E93' }}>Free to register — pay as you go for certificates and notifications</p>

            {error && (
              <div className="rounded-xl bg-red-50 p-3 mb-6 text-sm text-red-600 border border-red-200" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
                  Full Name
                </label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="brutalist-input"
                  placeholder="e.g. Michael Allan"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
                  Company Name
                </label>
                <input
                  data-testid="register-company-input"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="brutalist-input"
                  placeholder="e.g. Allan Electrical Ltd"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
                  Email
                </label>
                <input
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brutalist-input"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
                  Password
                </label>
                <input
                  data-testid="register-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="brutalist-input"
                  placeholder="Min. 6 characters"
                  required
                />
              </div>
              <button
                data-testid="register-submit-button"
                type="submit"
                disabled={loading}
                className="brutalist-btn w-full"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <p className="mt-6 text-sm text-[#71717A] text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-[#00A3E0] font-semibold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
